// Morning brief contract. Shared by the Worker (validation, storage), the
// client (rendering) and the routine prompt (docs/routines/morning-brief.prompt.md).
// Any change here must be mirrored in the prompt's JSON shape.

export type BriefTone = 'ok' | 'warn' | 'bad';
export type BriefEntityKind = 'task' | 'lead' | 'invoice' | 'project' | 'client' | 'hold';
export interface BriefLink { kind: BriefEntityKind; id: string }

export interface BriefItem {
  text: string;                 // one sentence, <= 200 chars
  tone?: BriefTone;
  link?: BriefLink;             // must reference an id present in the facts pack
  pattern?: boolean;            // italic "pattern" line (no bullet), at most one per section
  amount_cents?: number;        // server-attached for invoice links; never trusted from the routine
}

export const BRIEF_SECTION_KEYS = ['since', 'week_ahead', 'chase', 'sitting', 'pipeline'] as const;
export type BriefSectionKey = typeof BRIEF_SECTION_KEYS[number];
export interface BriefSection { count_label?: string; items: BriefItem[] }  // count_label e.g. "4 done", "$6,400", <= 40 chars

export interface MorningBriefContent {
  headline: string;             // <= 240 chars
  since_label: string;          // "Since Friday" | "Since yesterday" — copied from facts.since.label
  sections: Record<BriefSectionKey, BriefSection>;   // 0–5 items each; pipeline 1–2
  one_thing: { text: string; link?: BriefLink; amount_cents?: number };
}

export type BriefStatus = 'ok' | 'failed';
export type BriefVote = 'useful' | 'off';

export interface MorningBriefRow {
  id: string;
  brief_date: string;           // Melbourne YYYY-MM-DD the brief is for
  generated_at: number;         // ms, when the routine posted it
  status: BriefStatus;
  model: string | null;         // as reported by the routine, e.g. "claude-opus-5-5"
  source: 'routine' | 'manual';
  error: string | null;         // for status 'failed'
  content: MorningBriefContent | null;
  vote: BriefVote | null;
  item_count: number;           // server-computed
}

/** What Overview receives inside the aggregate payload under `morning_brief`. */
export interface OverviewMorningBrief {
  latest: MorningBriefRow | null;        // most recent row by brief_date, any status
  latest_ok: MorningBriefRow | null;     // most recent status 'ok' row (what the panel renders)
  today: string;                         // Melbourne date
  missed_today: boolean;                 // weekday, automation enabled, nothing received by the midday check
  regenerate_requested: boolean;
  routine_url: string | null;            // from automation config
  enabled: boolean;
}

/** Body the routine POSTs. */
export interface MorningBriefSubmission {
  brief_date: string;
  status: BriefStatus;
  model?: string;
  error?: string;               // required when status === 'failed'
  content?: MorningBriefContent; // required when status === 'ok'
}

export const BRIEF_LIMITS = {
  headline: 240, item_text: 200, count_label: 40, items_per_section: 5, focus_notes: 500, routine_url: 300, error: 500,
} as const;

export interface MorningBriefFacts {
  brief_date: string; weekday: string;
  since: { label: string; from: string; to: string };
  focus_notes: string | null;
  /** Private context since the previous working day; absent in older stored briefs. */
  journal?: Array<{ date: string; time: string; text: string }>;
  previous_brief: null | { brief_date: string; headline: string; one_thing: { text: string; link: BriefLink | null }; one_thing_done: boolean | null };
  completed_tasks: Array<{ id: string; title: string; project_id: string | null; project_name: string | null; client_name: string | null; completed_at: number; followup: boolean }>;
  activity_counts: Array<{ kind: string; count: number }>;
  tasks: {
    open: number; due_today: number;
    overdue: Array<{ id: string; title: string; due_date: string; days_overdue: number; project_name: string | null; priority: number; briefs_seen: number }>;
    due_this_week: Array<{ id: string; title: string; due_date: string; project_name: string | null }>;
  };
  week_ahead: Array<{ date: string; end_date?: string; kind: 'shoot' | 'edit' | 'due' | 'hold' | 'lead_followup' | 'task_due'; title: string; project_id?: string; lead_id?: string; hold_id?: string; task_id?: string; crew_booked?: number }>;
  chase: {
    overdue_invoices: Array<{ id: string; invoice_number: string; client_name: string; project_id: string | null; amount_cents: number; gst_cents: number; due_date: string | null; days_overdue: number; reminder_stage: string | null; snoozed_until: string | null }>;
    followups_due: Array<{ lead_id: string; name: string; company: string | null; status: string; followup_date: string | null; days_overdue: number | null; value_cents: number | null }>;
    proposals_waiting: Array<{ lead_id: string; name: string; company: string | null; status: string; days_in_status: number; value_cents: number | null }>;
    waiting_on_client: Array<{ project_id: string; name: string; client_name: string; since: string; nudges: number }>;
  };
  sitting: {
    stale_tasks: Array<{ id: string; title: string; due_date: string; days_overdue: number; briefs_seen: number; project_name: string | null }>;
    leads_no_followup: Array<{ lead_id: string; name: string; company: string | null; status: string; enquiry_date: string | null; days_since_enquiry: number | null }>;
    leads_untouched: Array<{ lead_id: string; name: string; status: string; days_since_update: number; value_cents: number | null }>;
    draft_invoices: Array<{ id: string; invoice_number: string | null; client_name: string; project_name: string | null; amount_cents: number; age_days: number }>;
    delivered_without_invoice: Array<{ project_id: string; name: string; client_name: string; delivered_date: string | null }>;
    unconfirmed_holds: Array<{ hold_id: string; title: string; start_date: string; end_date: string | null; client_text: string | null }>;
  };
  pipeline: {
    month: string;
    targets: Array<{ metric: string; target: number; actual: number; pct: number; on_pace: boolean; days_left: number }>;
    open_leads_by_status: Array<{ status: string; count: number; value_cents: number }>;
    enquiries_last_7_days: number; days_since_last_enquiry: number | null; jobs_won_last_30_days: number;
    outstanding_cents: number | null; overdue_cents: number | null;
    /** Locked-in leads whose start date (resurface_date) has arrived: the
        brief says "Convert to project". Optional — briefs stored before
        this field existed don't carry it. */
    locked_in_due?: Array<{ lead_id: string; name: string; company: string | null; status: string; resurface_date: string; days_since_start: number; value_cents: number | null }>;
  };
  ids: { task: string[]; lead: string[]; invoice: string[]; project: string[]; client: string[]; hold: string[] };
  invoice_amounts: Record<string, number>;
}

const TONES: BriefTone[] = ['ok', 'warn', 'bad'];
const KINDS: BriefEntityKind[] = ['task', 'lead', 'invoice', 'project', 'client', 'hold'];
const STATUSES: BriefStatus[] = ['ok', 'failed'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function containsRoleKey(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsRoleKey);
  if (!isRecord(value)) return false;
  if (Object.prototype.hasOwnProperty.call(value, 'role')) return true;
  return Object.values(value).some(containsRoleKey);
}

function asLimitedString(value: unknown, max: number, missing: string, tooLong: string): string | null {
  if (typeof value !== 'string' || value.trim() === '') throw new Error(missing);
  if (value.length > max) throw new Error(tooLong);
  return value;
}

function parseLink(value: unknown): BriefLink | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value) || typeof value.id !== 'string' || !KINDS.includes(value.kind as BriefEntityKind)) {
    throw new Error('invalid link');
  }
  return { kind: value.kind as BriefEntityKind, id: value.id };
}

function validateItem(value: unknown, path = 'item'): BriefItem {
  if (!isRecord(value)) throw new Error(`${path} invalid`);
  const item: BriefItem = { text: asLimitedString(value.text, BRIEF_LIMITS.item_text, 'item text required', 'item text too long')! };
  if (value.tone !== undefined) {
    if (!TONES.includes(value.tone as BriefTone)) throw new Error('invalid tone');
    item.tone = value.tone as BriefTone;
  }
  const link = parseLink(value.link);
  if (link) item.link = link;
  if (value.pattern !== undefined) {
    if (typeof value.pattern !== 'boolean') throw new Error('pattern must be boolean');
    item.pattern = value.pattern;
  }
  if (value.amount_cents !== undefined) {
    if (typeof value.amount_cents !== 'number' || !Number.isFinite(value.amount_cents)) throw new Error('amount_cents must be number');
    item.amount_cents = value.amount_cents;
  }
  return item;
}

function validateContent(value: unknown): MorningBriefContent {
  if (!isRecord(value)) throw new Error('content required');
  const sections = value.sections;
  if (!isRecord(sections)) throw new Error('sections required');
  const keys = Object.keys(sections);
  if (keys.some((key) => !BRIEF_SECTION_KEYS.includes(key as BriefSectionKey))) throw new Error('bad section key');
  if (BRIEF_SECTION_KEYS.some((key) => !Object.prototype.hasOwnProperty.call(sections, key))) throw new Error('missing sections');
  const parsedSections = {} as Record<BriefSectionKey, BriefSection>;
  for (const key of BRIEF_SECTION_KEYS) {
    const section = sections[key];
    if (!isRecord(section) || !Array.isArray(section.items)) throw new Error('section items required');
    if (section.items.length > BRIEF_LIMITS.items_per_section) throw new Error('too many items');
    if (key === 'pipeline' && section.items.length > 2) throw new Error('too many pipeline items');
    if (section.count_label !== undefined && (typeof section.count_label !== 'string' || section.count_label.length > BRIEF_LIMITS.count_label)) {
      throw new Error('count_label too long');
    }
    const items = section.items.map((item) => validateItem(item));
    if (items.filter((item) => item.pattern).length > 1) throw new Error('too many patterns');
    parsedSections[key] = { items, ...(section.count_label !== undefined ? { count_label: section.count_label } : {}) };
  }
  const oneThing = value.one_thing;
  if (!isRecord(oneThing)) throw new Error('one_thing required');
  const link = parseLink(oneThing.link);
  return {
    headline: asLimitedString(value.headline, BRIEF_LIMITS.headline, 'headline required', 'headline too long')!,
    since_label: asLimitedString(value.since_label, BRIEF_LIMITS.count_label, 'since_label required', 'since_label too long')!,
    sections: parsedSections,
    one_thing: {
      text: asLimitedString(oneThing.text, BRIEF_LIMITS.item_text, 'one_thing text required', 'item text too long')!,
      ...(link ? { link } : {}),
      ...(typeof oneThing.amount_cents === 'number' ? { amount_cents: oneThing.amount_cents } : {}),
    },
  };
}

export function validateSubmission(body: unknown): { ok: true; value: MorningBriefSubmission } | { ok: false; error: string } {
  try {
    if (!isRecord(body)) throw new Error('body must be object');
    if (containsRoleKey(body)) throw new Error('role not allowed');
    if (typeof body.brief_date !== 'string' || !DATE_RE.test(body.brief_date)) throw new Error('brief_date required');
    if (!STATUSES.includes(body.status as BriefStatus)) throw new Error('invalid status');
    const value: MorningBriefSubmission = { brief_date: body.brief_date, status: body.status as BriefStatus };
    if (body.model !== undefined) {
      if (typeof body.model !== 'string') throw new Error('model must be string');
      value.model = body.model.slice(0, 120);
    }
    if (value.status === 'failed') {
      value.error = asLimitedString(body.error, BRIEF_LIMITS.error, 'error required', 'error too long')!;
    } else {
      if (body.content === undefined) throw new Error('content required');
      value.content = validateContent(body.content);
    }
    return { ok: true, value };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'invalid submission' };
  }
}

function sanitizeItem(
  item: BriefItem,
  allowedIds: Map<BriefEntityKind, Set<string>>,
  invoiceAmounts: Map<string, number>,
): { item: BriefItem; dropped: number } {
  const next: BriefItem = { text: item.text };
  if (item.tone) next.tone = item.tone;
  if (item.pattern) next.pattern = item.pattern;
  let dropped = 0;
  if (item.link && allowedIds.get(item.link.kind)?.has(item.link.id)) {
    next.link = { ...item.link };
    if (item.link.kind === 'invoice') {
      const amount = invoiceAmounts.get(item.link.id);
      if (amount !== undefined) next.amount_cents = amount;
    }
  } else if (item.link) {
    dropped = 1;
  }
  return { item: next, dropped };
}

export function sanitizeContent(
  content: MorningBriefContent,
  allowedIds: Map<BriefEntityKind, Set<string>>,
  invoiceAmounts: Map<string, number>,
  sinceLabel = content.since_label,
): { content: MorningBriefContent; dropped_links: number } {
  let dropped_links = 0;
  const sections = {} as Record<BriefSectionKey, BriefSection>;
  for (const key of BRIEF_SECTION_KEYS) {
    const items = content.sections[key].items.map((item) => {
      const sanitized = sanitizeItem(item, allowedIds, invoiceAmounts);
      dropped_links += sanitized.dropped;
      return sanitized.item;
    });
    sections[key] = { items, ...(content.sections[key].count_label ? { count_label: content.sections[key].count_label } : {}) };
  }
  const oneThing = sanitizeItem({ text: content.one_thing.text, link: content.one_thing.link, amount_cents: content.one_thing.amount_cents }, allowedIds, invoiceAmounts);
  dropped_links += oneThing.dropped;
  return {
    content: { headline: content.headline, since_label: sinceLabel, sections, one_thing: oneThing.item },
    dropped_links,
  };
}
